import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const CarrickfergusChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Relief Carrickfergus | Back Pain Treatment Castle Town</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Carrickfergus from Belfast clinic (14 miles). Neuroscience-based approach for fibromyalgia, back pain, arthritis. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Carrickfergus, fibromyalgia hypnotherapy Carrickfergus, pain management castle town, back pain treatment Carrickfergus, arthritis pain Carrickfergus" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/carrickfergus-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Relief Carrickfergus | Back Pain Treatment Castle Town" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Carrickfergus. Neuroscience-based approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Carrickfergus",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Carrickfergus from Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, migraines, and hospitality worker pain with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/carrickfergus-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Carrickfergus",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with chronic pain from hospitality and tourism work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for chronic pain related to hospitality and tourism work. Many Carrickfergus residents work in hotels, restaurants, or as tour guides at the castle, involving standing all day, carrying heavy loads, repetitive movements, and physical demands that contribute to chronic back pain, foot pain, and repetitive strain. Hypnotherapy works by calming the nervous system that amplifies pain signals, reducing muscle tension, improving sleep quality, and helping your brain develop healthier pain processing patterns through neuroplasticity."
                }
              },
              {
                "@type": "Question",
                "name": "How does hypnotherapy work with NHS pain management in Carrickfergus?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Carrickfergus clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Carrickfergus for chronic pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 14 miles from Carrickfergus via the M5 and M2, then south to the Ormeau Road, taking just 25-30 minutes by car. This ultra-convenient distance makes regular pain management appointments highly practical, even for those with mobility limitations or chronic pain that makes longer journeys difficult. Many Carrickfergus clients appreciate the short, manageable drive for consistent treatment."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online chronic pain treatment for Carrickfergus residents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Carrickfergus residents who prefer online treatment or cannot make even the short drive to Belfast during severe pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, shift workers with irregular schedules, or anyone who wants maximum flexibility."
                }
              },
              {
                "@type": "Question",
                "name": "What chronic pain conditions do you treat for Carrickfergus clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Carrickfergus clients experiencing fibromyalgia, chronic back pain (particularly hospitality and tourism industry-related), arthritis, migraines, neuropathic pain, foot pain from standing all day, repetitive strain injuries from carrying loads and physical work, and IBS pain. The neuroscience-based approach addresses the nervous system sensitization underlying these conditions, helping to reduce pain perception and improve daily functioning so you can continue working in demanding roles."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for chronic pain management?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, your work demands, and your goals. The 4-session package provides excellent value while ensuring commitment to the process."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Relief for Carrickfergus
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Carrickfergus from Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">14 miles</span> from Carrickfergus • <span className="font-semibold">25-30 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Online sessions available for Carrickfergus residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Carrickfergus
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Carrickfergus from our Belfast clinic, located just 14 miles southwest - one of the most convenient therapy journeys in Northern Ireland at only 25-30 minutes. This ultra-short distance makes regular pain management appointments highly practical, even for those with chronic pain conditions that make longer travel difficult.
              </p>
              <p>
                Living with chronic pain in Carrickfergus's tourism economy comes with unique physical demands. Many residents work in hospitality - standing all day in hotels and restaurants, carrying heavy loads as tour guides at the castle, repetitive movements serving customers. The tourism industry's physical requirements, combined with seasonal employment stress and the need to maintain a professional demeanor while in pain, create significant challenges for pain sufferers.
              </p>
              <p>
                Modern pain neuroscience shows us that chronic pain is processed in the nervous system, not just the injury site. When we're stressed from work demands, worried about seasonal employment, or pushing through pain to maintain customer service standards, our nervous system becomes hypersensitive and amplifies pain signals. This is where solution-focused hypnotherapy excels. By calming your overactive alarm system, teaching your brain healthier pain processing patterns, and addressing the stress that intensifies pain, many clients experience significant reductions in pain levels.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand the physical toll of standing all day, the repetitive strain from hospitality work, and the frustration when pain medication only takes the edge off. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you manage pain, continue working, and maintain quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why standing all day or carrying loads can trigger disproportionate pain responses in hospitality workers.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from demanding customer service, seasonal employment worry, or maintaining professionalism while in pain keeps your survival system on high alert, which keeps pain signals turned up.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways. The more they're used, the easier they fire. But neuroplasticity also means we can create new, healthier pathways through hypnotherapy.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Carrickfergus residents dealing with the physical demands of tourism work and the stress of maintaining professional service while in pain.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways discovered in neuroscience research.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use during breaks at work, before shifts, or at home to manage pain flare-ups, reduce stress, and improve sleep - all factors that influence pain levels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. A major review of over 7,000 children with chronic abdominal pain found hypnotherapy 68% more effective than no treatment - outperforming dietary and medication-based approaches.
              </p>
              <p className="text-gray-700 text-lg">
                For Carrickfergus residents waiting months for Northern Trust pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NHS-Complementary Positioning */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, many Carrickfergus residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Chronic Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain, fatigue, and sensitivity. Hypnotherapy addresses the nervous system sensitization underlying fibromyalgia symptoms and helps improve sleep quality.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain, sciatica. Particularly relevant for hospitality workers standing all day or carrying heavy loads. Calming stress significantly reduces back pain intensity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Arthritis Pain</h3>
                <p className="text-gray-700">Osteoarthritis and rheumatoid arthritis pain management. Hypnotherapy helps reduce pain perception, improve mobility, and maintain work capacity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Migraines & Headaches</h3>
                <p className="text-gray-700">Chronic migraine, tension headaches. Stress reduction and trigger management through hypnotherapy can reduce frequency and intensity significantly.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Foot & Leg Pain</h3>
                <p className="text-gray-700">Plantar fasciitis, chronic foot pain from standing. Common among hospitality and tourism workers. Hypnotherapy reduces pain amplification and improves tolerance.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Repetitive Strain Injuries</h3>
                <p className="text-gray-700">RSI, carpal tunnel, work-related pain from repetitive movements. Hypnotherapy works with pain gate control to reduce neuropathic pain signals.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Carrickfergus Clients Choose Sharon Clare for Pain Management
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  15 Years Nursing Experience
                </h3>
                <p className="text-gray-700">
                  With 15 years at Belfast City Hospital, I understand pain conditions from a medical perspective. I've seen firsthand the physical toll of demanding jobs, the frustration of chronic pain, and the limitations of medication-only approaches. This medical background brings credibility and understanding to chronic pain treatment that many Carrickfergus clients find reassuring.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Tourism & Hospitality Pain
                </h3>
                <p className="text-gray-700">
                  I understand the unique pain patterns facing Carrickfergus's tourism economy - standing all day in hotels and restaurants, carrying heavy loads as castle tour guides, repetitive customer service movements, the pressure to maintain professionalism while in pain. The solution-focused approach addresses both the physical and stress components of hospitality work.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Ultra-Convenient Location
                </h3>
                <p className="text-gray-700">
                  At just 14 miles from Carrickfergus (25-30 minutes), the Belfast clinic is one of the most accessible therapy locations in Northern Ireland. This ultra-short journey makes regular pain management appointments practical even for those with mobility limitations or severe pain that makes longer travel difficult.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Online Sessions Available
                </h3>
                <p className="text-gray-700">
                  If traveling even 14 miles is difficult during severe pain flare-ups, secure online sessions via video call provide the same professional treatment from your home in Carrickfergus. This flexibility is ideal for shift workers, those with mobility limitations, or anyone experiencing severe pain days.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Scheduling
                </h3>
                <p className="text-gray-700">
                  Understanding the demands of hospitality work with shift patterns, weekend work, and seasonal pressures. Flexible appointment times help you prioritize pain management without compromising your employment or income.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Neuroscience Approach
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy combines the latest pain neuroscience research with proven therapeutic techniques. Rather than dwelling on pain history, we focus on solutions, building resilience, and practical strategies for managing pain while continuing to work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Carrickfergus
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M5 and M2 into Belfast, then head south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 14 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 25-30 minutes in normal traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">By public transport:</span> Botanic and City Hospital are the closest train stations, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Ultra-Convenient Access</h3>
                <p className="text-gray-700">
                  At just 14 miles, Carrickfergus residents have one of the shortest therapy journeys in Northern Ireland. The quick 25-30 minute drive makes regular pain management appointments highly practical, even for those managing mobility challenges or chronic pain conditions. Many clients schedule appointments around work shifts or on days off.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                If traveling is difficult due to your pain condition or shift work schedule, I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Carrickfergus. This option is ideal for those experiencing pain flare-ups, mobility concerns, or anyone who prefers online therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Carrickfergus Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in hospitality at the castle, I was on my feet all day with chronic back pain. The short drive to Belfast made regular sessions possible. Sharon's hypnotherapy helped me manage the pain without strong medication. I can work full shifts now without struggling. Her nursing background meant she understood the physical demands."
                </p>
                <p className="font-semibold text-gray-900">L.T., Carrickfergus - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My fibromyalgia was making work impossible. The online sessions were perfect for my shift pattern at the hotel. Sharon helped me understand how stress was amplifying my pain. After 7 sessions, I've reduced my painkillers significantly and I'm functioning so much better. The flexible scheduling saved my job."
                </p>
                <p className="font-semibold text-gray-900">K.M., Carrickfergus - Fibromyalgia</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Chronic foot pain from standing all day as a tour guide was unbearable. The 20-minute drive to Belfast was nothing compared to the relief I got. Hypnotherapy gave me tools to manage pain during shifts. My pain hasn't disappeared but it's manageable now, and I can enjoy my work again without constant discomfort."
                </p>
                <p className="font-semibold text-gray-900">D.S., Carrickfergus - Chronic Foot Pain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Carrickfergus Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with chronic pain from hospitality and tourism work?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for chronic pain related to hospitality and tourism work. Many Carrickfergus residents work in hotels, restaurants, or as tour guides at the castle, involving standing all day, carrying heavy loads, repetitive movements, and physical demands that contribute to chronic back pain, foot pain, and repetitive strain. Hypnotherapy works by calming the nervous system that amplifies pain signals, reducing muscle tension, improving sleep quality, and helping your brain develop healthier pain processing patterns through neuroplasticity.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does hypnotherapy work with NHS pain management in Carrickfergus?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Carrickfergus clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Carrickfergus for chronic pain treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 14 miles from Carrickfergus via the M5 and M2, then south to the Ormeau Road, taking just 25-30 minutes by car. This ultra-convenient distance makes regular pain management appointments highly practical, even for those with mobility limitations or chronic pain that makes longer journeys difficult. Many Carrickfergus clients appreciate the short, manageable drive for consistent treatment without requiring significant time off work.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online chronic pain treatment for Carrickfergus residents?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Carrickfergus residents who prefer online treatment or cannot make even the short drive to Belfast during severe pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, shift workers with irregular schedules, or anyone who wants maximum flexibility. You can switch between in-person and online sessions based on your needs.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What chronic pain conditions do you treat for Carrickfergus clients?
                </h3>
                <p className="text-gray-700">
                  I work with Carrickfergus clients experiencing fibromyalgia, chronic back pain (particularly hospitality and tourism industry-related), arthritis, migraines, neuropathic pain, foot pain from standing all day, repetitive strain injuries from carrying loads and physical work, and IBS pain. The neuroscience-based approach addresses the nervous system sensitization underlying these conditions, helping to reduce pain perception and improve daily functioning so you can continue working in demanding roles.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for chronic pain management?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, your work demands, and your goals. The 4-session package provides excellent value while ensuring commitment to the process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default CarrickfergusChronicPain;
